import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Edge,
  Node,
  NodeTypes,
  ReactFlow,
  useEdgesState,
  useNodesState,
} from '@xyflow/react';

import '@xyflow/react/dist/style.css';
import { Box, IconButton, styled, Tooltip } from '@mui/material';
import WorkflowStepEdge from './WorkflowStepEdge';
import workflowChartUtils from '../../../services/workflowChartUtils';
import { useTranslation } from 'react-i18next';
import WorkflowStepNode from './WorkflowStepNode';
import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import { isEqual } from 'lodash';
import WorkflowAddButtonNode from './WorkflowAddButtonNode';
import WorkflowBranchingEdge from './WorkflowBranchingEdge';
import WorkflowSmallStepNode from './WorkflowSmallStepNode';

export interface WorkflowTransition {
  id: string;
  name?: string;
  level: number;
}

export interface WorkflowChartItem {
  id: string;
  title: string;
  actions: string[];
  row: number;
  colInfo?: { col: number; totalCols: number };
  parentId?: string;
  onSuccess?: WorkflowTransition;
  onFailure?: WorkflowTransition;
  onCancel?: WorkflowTransition;
  onNext?: WorkflowTransition[];
  isAddButton?: boolean;
}

export interface WorkflowFlowChartProps {
  items: WorkflowChartItem[];
  selectedId: string;
  handleStepClick: (id: string) => boolean;
  handleAddStepClick: () => void;
  handleAddStepToBranchClick?: (branchName: string, parentId: string) => void;
  removedChartItemId?: string;
  showAddStepButton?: boolean;
}

type WorkflowFlowNodeData = {
  row: number;
  title?: string;
  parentId?: string;
  isAddButton?: boolean;
  [key: string]: unknown;
};

type WorkflowFlowNode = Node<WorkflowFlowNodeData>;

const PREFIX = 'WorkflowFlowChart';

const classes = {
  container: `${PREFIX}-container`,
  addIconContainer: `${PREFIX}-addIconContainer`,
  addIcon: `${PREFIX}-addIcon`,
};

const StyledBox = styled(Box)(({ theme }) => ({
  [`& .${classes.container}`]: {
    padding: theme.spacing(2),
    paddingBottom: 0,
  },
  [`& .${classes.addIconContainer}`]: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: theme.spacing(3),
  },
  [`& .${classes.addIcon}`]: {
    color: theme.palette.primary.main,
    marginRight: 35,
  },
}));

export const nodeName = 'workflowStepNode';
export const smallNodeName = 'workflowSmallStepNode';
export const addButtonNodeName = 'workflowAddButtonNode';
export const edgeName = 'workflowStepEdge';
export const branchingEdgeName = 'workflowBranchingEdge';
export const nodeGap = 70;

const WorkflowFlowChart = ({
  items,
  selectedId,
  handleStepClick,
  handleAddStepClick,
  handleAddStepToBranchClick,
  removedChartItemId,
  showAddStepButton = true,
}: WorkflowFlowChartProps) => {
  const { t } = useTranslation('common');
  const nodeTypes: NodeTypes = useMemo(
    () => ({
      [nodeName]: WorkflowStepNode,
      [smallNodeName]: WorkflowSmallStepNode,
      [addButtonNodeName]: WorkflowAddButtonNode,
    }),
    [],
  );
  const edgeTypes = useMemo(
    () => ({
      [edgeName]: WorkflowStepEdge,
      [branchingEdgeName]: WorkflowBranchingEdge,
    }),
    [],
  );
  const [nodes, setNodes, onNodesChange] = useNodesState<WorkflowFlowNode>([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>([]);
  const [nodeHeights, setNodeHeights] = useState<{ [key: string]: number }>({});
  const [flowChartHeight, setFlowChartHeight] = useState(600);
  const [nodeCount, setNodeCount] = useState(0);
  const nodesRef = useRef(nodes);

  useEffect(() => {
    nodesRef.current = nodes;
  }, [nodes]);

  // Handler for height changes
  const handleNodeHeightChange = useCallback(
    (nodeId: string, height: number) => {
      setNodeHeights((prev: any) => {
        // Only update if height actually changed
        if (prev[nodeId] !== height) {
          const newHeights = { ...prev, [nodeId]: height };
          return newHeights;
        }
        return prev;
      });
    },
    [],
  );

  // Update node positions when heights change
  useEffect(() => {
    if (Object.keys(nodeHeights).length === 0) return;

    // Create a copy of the current nodes
    const currentNodes = nodesRef.current;
    if (currentNodes.length === 0) return;

    const measuredNodeIds = new Set(Object.keys(nodeHeights));
    if (!currentNodes.some((node) => measuredNodeIds.has(node.id))) return;

    const updatedNodes = currentNodes.map((node) => ({ ...node }));
    let currentY = 0;
    let groupedNodes = workflowChartUtils.groupByRow(
      updatedNodes,
      (item) => item.data.row,
    );

    groupedNodes.forEach((nodes, index) => {
      let rowHeight = 0;
      nodes.forEach((node, itemIndex) => {
        node.position = {
          ...node.position,
          y: currentY,
        };
        const nodeHeight = nodeHeights[node.id] || 0;
        if (nodeHeight > rowHeight) {
          rowHeight = nodeHeight;
        }
      });
      currentY += rowHeight + nodeGap;
    });
    setFlowChartHeight(currentY - nodeGap + 50);
    // React flow chart can get corrupted if the height
    // isnt set before updating the nodes
    const updatedNodePositions = new Map(
      updatedNodes.map((node) => [node.id, node.position]),
    );
    const timeout = window.setTimeout(() => {
      setNodes((currentNodes) => {
        const currentNodeIds = new Set(currentNodes.map((node) => node.id));
        const isSameChart =
          currentNodes.length === updatedNodes.length &&
          updatedNodes.every((node) => currentNodeIds.has(node.id));

        if (!isSameChart) {
          return currentNodes;
        }

        let hasPositionChange = false;
        const nextNodes = currentNodes.map((node) => {
          const nextPosition = updatedNodePositions.get(node.id);
          if (!nextPosition) {
            return node;
          }

          if (
            node.position.x === nextPosition.x &&
            node.position.y === nextPosition.y
          ) {
            return node;
          }

          hasPositionChange = true;
          return {
            ...node,
            position: nextPosition,
          };
        });

        return hasPositionChange ? nextNodes : currentNodes;
      });
    }, 0);
    return () => window.clearTimeout(timeout);
  }, [nodeHeights, nodeCount, setNodes]);

  const nodesAndEdges = useMemo(() => {
    let nodes = workflowChartUtils.getNodes(items, selectedId, nodeHeights);
    nodes.forEach((node) => {
      node.data.onHeightChange = handleNodeHeightChange;
    });

    return {
      nodes,
      edges: workflowChartUtils.getEdges(items, t),
    };
  }, [items, selectedId]);

  useEffect(() => {
    const nextNodes = nodesAndEdges.nodes as WorkflowFlowNode[];
    setNodes((currentNodes) => {
      const currentNodesById = new Map(
        currentNodes.map((node) => [node.id, node]),
      );

      return nextNodes.map((node) => {
        const currentNode = currentNodesById.get(node.id);
        if (!currentNode) {
          return node;
        }

        return {
          ...node,
          measured: currentNode.measured,
          width: currentNode.width,
          height: currentNode.height,
          initialWidth: currentNode.initialWidth,
          initialHeight: currentNode.initialHeight,
        };
      });
    });
    setEdges(nodesAndEdges.edges);
    const newNodeCount = nodesAndEdges.nodes.length;
    if (newNodeCount !== nodeCount) {
      setNodeCount(newNodeCount);
    }
  }, [nodesAndEdges]);

  useEffect(() => {
    if (removedChartItemId) {
      const updatedNodeHeights = {
        ...nodeHeights,
        [removedChartItemId]: 0,
      };
      setNodeHeights(updatedNodeHeights);
    }
  }, [removedChartItemId]);

  // Handler for node clicks
  const onNodeClick = (_: React.MouseEvent, node: WorkflowFlowNode) => {
    if (node.data.isAddButton) {
      handleAddStepToBranchClick?.(
        node.data.title as string,
        node.data.parentId as string,
      );
      return;
    }
    const stepChanged = handleStepClick(node.id);
    if (!stepChanged) {
      setNodes((nodes) => {
        const updatedNodes = nodes.map((node, index) => ({
          ...node,
          selected: node.id === selectedId,
        }));
        return updatedNodes;
      });
    }
  };

  const onPaneClick = useCallback(() => {
    // Prevent clicks outside of the node from de-selecting it
    setTimeout(() => {
      setNodes(nodes);
    }, 0);
  }, [nodes, setNodes]);

  return (
    <StyledBox>
      <Box className={classes.container} sx={{ height: flowChartHeight }}>
        <ReactFlow
          nodes={nodes}
          edges={edges}
          nodeTypes={nodeTypes}
          edgeTypes={edgeTypes}
          nodesDraggable={false}
          nodesConnectable={false}
          zoomOnScroll={false}
          panOnScroll={false}
          panOnDrag={false}
          zoomOnDoubleClick={false}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onNodeClick={onNodeClick}
          onPaneClick={onPaneClick}
        />
      </Box>
      {showAddStepButton && (
        <Box className={classes.addIconContainer}>
          <Tooltip title={t('workflows.addWorkflowStep')}>
            <IconButton
              aria-label="add workflow"
              className={classes.addIcon}
              onClick={() => handleAddStepClick()}>
              <AddCircleOutlineOutlinedIcon />
            </IconButton>
          </Tooltip>
        </Box>
      )}
    </StyledBox>
  );
};

export const MemoizedWorkflowFlowChart = memo(
  WorkflowFlowChart,
  (prevProps, nextProps) => {
    return isEqual(prevProps, nextProps);
  },
);
